import { useEffect, useState } from 'react';

/**
 * Картинка работы с просмотром по нажатию. В списках и карточках показывается
 * уменьшенная копия (`?size=thumb`), а полный файл грузится только когда его
 * действительно открыли: на телефоне лента из полноразмерных картинок съедает
 * и трафик, и память.
 */
export function ZoomableImage({
  url,
  alt,
  className,
  thumb = true,
}: {
  url: string;
  alt: string;
  className?: string;
  /** `false` для крупных обложек: там уменьшенная копия уже заметно мылит. */
  thumb?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const preview = thumb ? withThumb(url) : url;

  return (
    <>
      <button
        type="button"
        className={`zoomable ${className ?? ''}`}
        aria-label={`${alt}: открыть крупно`}
        onClick={() => {
          setOpen(true);
        }}
      >
        <img src={preview} alt={alt} loading="lazy" />
        <span className="zoomable__hint" aria-hidden="true">
          ⤢
        </span>
      </button>
      {open && <ImageViewer url={url} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Просмотр во весь экран. Масштаб меняется щипком — это делает сам браузер,
 * поэтому картинка просто лежит в прокручиваемом слое, а не тянет за собой
 * библиотеку жестов.
 */
function ImageViewer({ url, alt, onClose }: { url: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    document.documentElement.classList.add('is-modal-open');
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      document.documentElement.classList.remove('is-modal-open');
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="image-viewer" role="dialog" aria-modal="true" aria-label={alt}>
      <button type="button" className="image-viewer__close" aria-label="Закрыть" onClick={onClose}>
        ×
      </button>
      <div className="image-viewer__canvas" onClick={onClose}>
        <img src={url} alt={alt} />
      </div>
    </div>
  );
}

/** Тот же адрес с уменьшенной копией: сервер отдаёт её по `?size=thumb`. */
function withThumb(url: string): string {
  return url.includes('?') ? `${url}&size=thumb` : `${url}?size=thumb`;
}
